import { computed, Injectable, OnDestroy, signal } from '@angular/core';
import { environment } from '@environment/environment';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';

export interface FeatureFlags {
  enableNotifications: boolean;
  enableComponentTree: boolean;
  enableMediaMalwareScan: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService implements OnDestroy {
  private client: LaunchDarkly.LDClient;

  private readonly _flags = signal<FeatureFlags>({
    enableNotifications: false,
    enableComponentTree: true,
    enableMediaMalwareScan: false
  });

  public readonly enableNotifications = computed(() => this._flags().enableNotifications);
  public readonly enableComponentTree = computed(() => this._flags().enableComponentTree);
  public readonly enableMediaMalwareScan = computed(() => this._flags().enableMediaMalwareScan);

  async ngOnDestroy() {
    await this.client.close();
  }

  async initialize(
    context: LaunchDarkly.LDContext = {
      kind: 'user',
      key: 'anonymous',
      anonymous: true
    }
  ): Promise<void> {
    if (!environment.launchDarklyClientSideId) {
      return;
    }

    const overrideContext = !environment.production ? window.__LD_INITIAL_CONTEXT__ : null;

    const initialContext = overrideContext ?? context;

    this.client = LaunchDarkly.initialize(environment.launchDarklyClientSideId, initialContext);

    await this.client
      .waitForInitialization(5)
      .then(() => {
        this.setupFlagListeners();
        this.setFlags();

        if (!environment.production) {
          window.__setLDContext = (ctx: LaunchDarkly.LDContext) => this.setContext(ctx);
          window.__getLDFlags = () => this._flags();
        }
      })
      .catch(() => {
        console.error('feature flags failed to initialise');
      });
  }

  /**
   * Use for automated tests
   * @param context
   */
  async setContext(context: LaunchDarkly.LDContext): Promise<void> {
    if (!this.client) return;

    try {
      await this.client.identify(context);
      this.setFlags();
    } catch (e) {
      console.error('Failed to update LD context', e);
    }
  }

  private setupFlagListeners(): void {
    this.client.on('change', () => {
      this.setFlags();
    });
  }

  private setFlags(): void {
    this._flags.set({
      enableNotifications: this.client.variation('enable-notifications', false),
      enableComponentTree: this.client.variation('enable-component-tree', true),
      enableMediaMalwareScan: this.client.variation(
        'enable-media-malware-scan',
        environment.features.enableMediaScanningMessage ?? false
      )
    });
  }
}
